
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux'

import { useAuth } from './UseAuth'
import { useProject } from './UseProject'
import { useTeams } from './UseTeam';

import History from '../Helpers/History'

import { setProgrammationAction } from '../Redux/Actions/ProgrammationActions'
import { getItemAndItsParentFromTaskList } from '../Helpers/ProgrammationHelper';
import { createGroup, createTask } from '../Services/ProgrammationManagerService';
import { createGroupAction, createTaskAction } from '../Redux/Actions/ProgrammationManagerActions';
import { fetchProgrammationSnapshot } from './programmationLoader';

interface Options {
  item_id: string
  schema?: 'macro' | 'lookahead' | 'weekly'
  mode?: 'create' | 'edit'
  noRedirect?: boolean
}

interface Error {
  field: string
  error: string
}
interface ContextValue {
  schema: 'macro' | 'lookahead' | 'weekly'
  mode: 'create' | 'edit'
  teams: any[]
  itemOpened: any | null
  programmationLoaded: boolean
  editingItem: boolean
  errors: Error[]
  loadProgrammation: () => void
  createNewTask: (parent?: any) => void
  createNewGroup: (parent?: any) => void
  openItem: (input: string | Options) => void
  closeItem: () => void
  updateItemField: (fieldName: string, value: string) => void
  patchItemOpened: (patch: any) => void
  setEditingItem: (editingItem: boolean) => void
  setError: (error: Error) => void
  unSetError: (field: string) => void
  clearErrors: () => void
}

const ProgrammationContext = createContext<ContextValue | undefined>(undefined)

export const ProgrammationProvider = ({ children }: { children: ReactNode }) => {

  const dispatch = useDispatch()

  const { ability } = useAuth()
  const { project_id, project, projectLoaded } = useProject()

  const { loadTeams } = useTeams()

  const programmationLoaded = useSelector((state: any) => state.ProgrammationReducer.loadStatus === 'loaded')
  const { documents } = useSelector((state: any) => state.ProgrammationReducer)
  const macroResult = useSelector((state: any) => state.MacroReducer.result)
  const lookaheadResult = useSelector((state: any) => state.LookaheadReducer.result)
  const weeklyResult = useSelector((state: any) => state.WeeklyReducer.result)

  const [ schema, setSchema ] = useState<'macro' | 'lookahead' | 'weekly'>('macro')
  const [ mode, setMode ] = useState<'create' | 'edit'>('edit')
  const [ itemOpened, setItemOpened ] = useState<any | null>(null)
  const [ editingItem, setEditingItem ] = useState(false)
  const [ errors, setErrors ] = useState<any[]>([])

  const loadProgrammation = async () => {

    dispatch({
      type: 'PROGRAMMATION_LOAD_START'
    })

    try {
      const snapshot = await fetchProgrammationSnapshot(project_id)

      dispatch(setProgrammationAction(snapshot))
    } catch (error) {
      dispatch({
        type: 'PROGRAMMATION_LOAD_ERROR',
        error: error instanceof Error ? error.message : 'unknown error',
      })
    }
  }

  const createNewTask = async (parent?: any) => {

    const [ error, data ] = await createTask(project_id, parent)

    if (error) return console.log(error)

    setMode('create')

    await dispatch(createTaskAction(data.task, data.taskList, data.parentTask))

    const task = data.task

    if (parent) {

      if (parent.isGroup) {

        task.parentType = 'group'
        task.groupId = parent._id
        task.parentPosition = parent.position
      } else {
        task.parentId = parent._id
      }
      task.parentName = parent.name
    }
    setItemOpened(task)

    // setItemOpened(data.task)
    // openItem(data.task._id)
  }

  const createNewGroup = async (parent?: any) => {

    const [ error, data ] = await createGroup(project_id, undefined, parent?._id)

    if (error) return console.log(error)

    setMode('create')
    setItemOpened({
      ...data.group,
      type: 'group',
      parentId: parent?._id,
    })

    dispatch(createGroupAction(data.taskList))
  }

  const openItem = (input: string | Options) => {

    let item_id: string
    let schema: 'macro' | 'lookahead' | 'weekly' = 'macro'
    let mode: 'create' | 'edit' = 'edit'
    let noRedirect = false

    if (typeof input === 'string') {
      item_id = input
    } else {
      item_id = input.item_id
      if (input.schema) schema = input.schema
      if (input.mode) mode = input.mode
      if (input.noRedirect) noRedirect = input.noRedirect
    }

    setSchema(schema)
    setMode(mode)

    let result = macroResult

    if (schema === 'lookahead') {

      result = lookaheadResult
    } else if (schema === 'weekly') {

      result = weeklyResult
    }

    const { item, parent } = getItemAndItsParentFromTaskList(result, item_id)

    if (parent) {

      item.parentPosition = parent?.position

      if (parent?.items) {

        item.parentType = 'group'
      } else {
        item.parentType = parent.type
      }
    }

    if (item.items) {

      item.type = 'group'

      item.parentId = parent?._id
      item.parentName = parent?.name

      setItemOpened(item)

      if (!noRedirect) History.push(`/project/${project._id}/programmation-${schema}?group_id=${item._id}`)
    } else {

      const selectedItem = { ...item }

      selectedItem.documents = documents.filter((document: any) => document.task === item._id)

      if (parent) {

        if (parent?.items) {

          selectedItem.groupId = parent?._id
          selectedItem.groupName = parent?.name
        } else {

          selectedItem.parentId = parent?._id
          selectedItem.parentName = parent?.name
        }
      }

      setItemOpened(selectedItem)

      if (!noRedirect) History.push(`/project/${project._id}/programmation-${schema}?task_id=${item._id}`)
    }
  }

  const updateItemField = async (fieldName: string, value: string) => {

    const item = {...itemOpened}

    item[fieldName] = value

    setItemOpened(item)
  }

  const patchItemOpened = (patch: any) => {

    const item = {
      ...itemOpened,
      ...patch
    }

    setItemOpened(item)
  }

  const closeItem = () => {

    setItemOpened(null)
  }

  const setError = (error: Error) => {

    setErrors([ ...errors, error ])
  }

  const unSetError = (field: string) => {

    setErrors(errors.filter((error: any) => error.field !== field))
  }

  const clearErrors = () => {

    setErrors([])
  }

  useEffect(() => {

    // const loadTeams = async () => {

    //   // await dispatch(TeamsActions.getResultAction(project_id))
    // }

    if (projectLoaded) {

      if (project.ability) ability.update(project.ability)

      loadTeams()
      loadProgrammation()
    }
  }, [projectLoaded])

  const value: ContextValue = {
    schema,
    mode,
    teams: [],
    itemOpened,
    programmationLoaded,
    editingItem,
    loadProgrammation,
    createNewTask,
    createNewGroup,
    errors,
    openItem,
    closeItem,
    updateItemField,
    patchItemOpened,
    setEditingItem,
    setError,
    unSetError,
    clearErrors,
  }

  return (
    <ProgrammationContext.Provider value={value}>
      {children}
    </ProgrammationContext.Provider>
  )
}

export const useProgrammation = (): ContextValue => {

  const context = useContext(ProgrammationContext)

  if (context === undefined) {

    throw new Error('useProgrammation must be used within a ProgrammationProvider')
  }

  return context
}
